import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { NavLink } from "react-router";

import { cn } from "@/lib/cn";

import type { SectionGroup } from "./navModel";

const ITEM_CLASS =
  "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors";

/**
 * The two rails' presentations, as MEASURED on the reference rather than
 * guessed (#908).
 *
 * They differ, and the difference is the reference's own: the Clinical library
 * rail draws its selected entry in the accent teal (`#dbefee` on `#1f5f5c`,
 * this theme's `accent`/`accent-foreground`) and shows no group heading; the
 * clinical-configuration rail draws its selected entry in the neutral
 * `secondary` (`#eef0f3`) under `BACKGROUND`/`INTERVIEW`/`SUMMARY` headings
 * that collapse. Normalising the two into one treatment would move one of them
 * away from the reference, so the surface says which it is and this table says
 * what that means. Nothing else in the rail is conditional.
 */
const SURFACES = {
  clinical: {
    active: "bg-secondary font-medium text-foreground",
    /** #489's disclosures: a real button inside the heading that names it. */
    headings: "disclosure",
  },
  library: {
    active: "bg-accent font-medium text-accent-foreground",
    /**
     * Hidden from the screen, kept in the accessibility tree — the treatment
     * `SidebarNav` already gives the primary navigation's unlabelled groups
     * (#516). There is nothing visible to collapse, so there is no control
     * either; deleting the heading outright would trade a structure a
     * screen-reader user moves through by heading for a bare list of links.
     */
    headings: "screen-reader-only",
  },
} as const satisfies Record<
  string,
  { active: string; headings: "disclosure" | "screen-reader-only" }
>;

export type SectionRailSurface = keyof typeof SURFACES;

const IDLE_CLASS =
  "text-muted-foreground hover:bg-secondary/60 hover:text-foreground";

/**
 * Grouped section navigation used inside Clinical configuration, Product
 * settings, and the shared library.
 *
 * These entries navigate for real. The destination page states plainly that its
 * editor arrives with a named later issue, which is honest about what exists
 * without hiding the shape of the product from the clinicians reviewing it.
 *
 * ## COLLAPSIBLE, AND WHY THE HEADER IS A BUTTON INSIDE A HEADING (#489)
 *
 * The group label used to be a bare `<h2>`. The ticket requires the groups to
 * "be expanded/collapsed by keyboard and expose correct accessibility state",
 * and the only construction that gives both is the disclosure pattern: a real
 * `<button>` — which is focusable, Enter/Space-operable and announced as a
 * control with no key handling of our own — nested inside the heading that
 * still names the group. Wrapping rather than replacing keeps the document
 * outline a screen-reader user navigates by; replacing the heading with a
 * button would buy the control by removing the landmark.
 *
 * `aria-expanded` states the group's own status and `aria-controls` names the
 * list it operates, so the relationship is readable rather than positional.
 *
 * DEFAULT EXPANDED, AND NOT PERSISTED. Every group opens on mount: a rail that
 * remembered a collapsed group across navigations would hide clinical sections
 * from the next visit with no visible cause, and the ticket asks for a
 * disclosure, not a preference. Collapsed groups are held per mount in this
 * component's own state, keyed by group label.
 *
 * COLLAPSED MEANS UNMOUNTED, not hidden. `getAllByRole("link")` inside this nav
 * is how `SectionSurface.test.tsx` counts what is on screen, and a `display:
 * none` list would keep those links in the accessibility tree while a clinician
 * could not reach them.
 *
 * ## DIRECTION-AGNOSTIC (#489)
 *
 * Every inline-axis class here is logical rather than physical — `border-e` and
 * `pe-4` instead of `border-r` and `pr-4` — so the rail sits on the correct
 * side under `dir="rtl"` without a second stylesheet. This is the bounded RTL
 * scope the ticket carries: the surfaces it changes are built direction-
 * agnostically. No application-wide locale or direction switch is introduced
 * here; that stays with #455 and #10.
 *
 * ## NAVLINK OWNS ACTIVE STATE OUTRIGHT (#516)
 *
 * This component carried an `activeSlug` escape hatch for one caller. #203's
 * project-root landing rendered a section in place while the address stayed on
 * the parent, where NavLink — which derives active state, and `aria-current`,
 * from the address alone — could mark nothing; that one entry was therefore
 * drawn as a plain `Link` marked active by hand. The project root renders the
 * Overview now, so no address mounts this rail without matching one of its
 * entries, and the second mechanism is gone rather than left unreachable.
 */
export function SectionRail({
  groups,
  basePath,
  ariaLabel,
  surface,
}: {
  groups: SectionGroup[];
  /** Prefix the slug is appended to, without a trailing slash. */
  basePath: string;
  ariaLabel: string;
  /**
   * Which of the reference's two rails this is. Required rather than defaulted:
   * `SURFACES` above records that the two are drawn differently on purpose, and
   * a default would silently give a third caller one of them.
   */
  surface: SectionRailSurface;
}) {
  const { active: activeClass, headings } = SURFACES[surface];
  const railId = useId();
  // Collapsed rather than expanded, so the default — every group open — is the
  // empty set and a group added later needs no entry to behave correctly.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  function toggle(label: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(label)) {
        next.add(label);
      }
      return next;
    });
  }

  return (
    <nav
      aria-label={ariaLabel}
      className="w-full shrink-0 border-b border-border pb-4 md:w-rail md:border-b-0 md:border-e md:pb-0 md:pe-4"
    >
      <div className="space-y-5">
        {groups.map((group) => {
          const isCollapsed =
            headings === "disclosure" && collapsed.has(group.label);
          const listId = `${railId}-${group.label.replace(/\W+/g, "-")}`;

          return (
            <div key={group.label} className="space-y-1">
              {headings === "screen-reader-only" ? (
                <h2 className="sr-only">{group.label}</h2>
              ) : (
                <h2>
                  <button
                    type="button"
                    aria-expanded={!isCollapsed}
                    aria-controls={listId}
                    onClick={() => toggle(group.label)}
                    className="flex w-full items-center gap-2 rounded-md px-3 pb-1 pt-1 text-start text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 transition-transform",
                        // Rotating toward the INLINE-START edge when collapsed,
                        // so the affordance points the same way relative to the
                        // text in both directions.
                        isCollapsed && "-rotate-90 rtl:rotate-90",
                      )}
                    />
                    <span className="min-w-0 flex-1">{group.label}</span>
                  </button>
                </h2>
              )}

              <div id={listId} hidden={isCollapsed}>
                {isCollapsed
                  ? null
                  : group.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <NavLink
                          key={item.slug}
                          to={`${basePath}/${item.slug}`}
                          className={({ isActive }) =>
                            cn(ITEM_CLASS, isActive ? activeClass : IDLE_CLASS)
                          }
                        >
                          {Icon ? (
                            <Icon
                              aria-hidden="true"
                              className="h-4 w-4 shrink-0"
                            />
                          ) : null}
                          <span className="min-w-0 truncate">{item.label}</span>
                        </NavLink>
                      );
                    })}
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
